/**
 * Rebuilds the paragraphs of a text that has the hard line breaks of the file it came from (a
 * PDF, a text file): one line break per visual line, and no blank line between paragraphs.
 * It is used when a file is read, so that the text kept (and sent to Tahiti) has real paragraphs.
 * Every character stays; only line breaks and spaces change, plus a hyphen that cuts a word.
 *
 * The result separates the paragraphs by a blank line. Consecutive items of a list stay on their
 * own lines inside one paragraph.
 */

/** A line that ends a sentence: `.`, `!`, `?`, `…`, `:`, `;`, `»` or `)`, then maybe quotes. */
const FIN_DE_PHRASE = /[.!?…:;»)][»"”’'\])]*$/u;
/** Opening marks that are not part of the first word. */
const OUVERTURE = /^[«"“‘'([\s]+/u;
/** `-`, `•`, `*`, `–`, `1.`, `1)`, `a)` and `(1)` at the start of a line. */
const PUCE = /^(?:[•▪●◦·]|[-*–—](?=\s)|\d{1,3}[.)](?=\s)|\(\d{1,3}\)(?=\s)|\p{L}\)(?=\s))/u;
/** Linking words after which a line cannot be a title: the sentence goes on. */
const MOTS_DE_LIAISON = new Set(
  (
    'et ou de du des d la le les l un une à au aux en que qu qui par pour sur sous dans avec sans ' +
    'mais donc car ni ce cet cette ces son sa ses leur leurs notre nos votre vos mon ma mes ton ta ' +
    'tes se s ne n y dont où si chez vers entre comme plus très the and of to for a'
  ).split(' '),
);
/** Abbreviations whose full stop does not end a sentence ("M.\nDupont"). */
const ABREVIATIONS = new Set(
  'm mme mlle mm dr pr me st ste cf art etc p pp n no vs env tel fig vol ex'.split(' '),
);
/** Words before a hyphen that is part of a compound word, not a cut ("peut-\nêtre"). */
const PREFIXES_COMPOSES = new Set(
  'est peut vice porte grand demi ex non mi semi quasi anti ci là après avant sous sur entre extra sans contre'.split(
    ' ',
  ),
);
const TRAIT_D_UNION = /\p{L}[-­]$/u;
const FIN_COUPE = /[-­]$/u;
const SEUIL_COURT = 0.6;

type Coupure = 'paragraphe' | 'puce' | 'espace' | 'mot-coupe' | 'colle';

/** The first letter of a line, past the opening quotes and brackets. */
function premiereLettre(ligne: string): string {
  return ligne.replace(OUVERTURE, '').slice(0, 1);
}
const estMinuscule = (c: string) => c !== '' && c === c.toLowerCase() && c !== c.toUpperCase();
const estMajuscule = (c: string) => c !== '' && c === c.toUpperCase() && c !== c.toLowerCase();

function dernierMot(ligne: string): string {
  const mots = ligne.split(/\s+/);
  return mots[mots.length - 1] ?? '';
}

/** The last word, in lower case, without the marks around it ("d'" gives "d"). */
function dernierMotNu(ligne: string): string {
  const nu = dernierMot(ligne).replace(/^[^\p{L}]+|[^\p{L}]+$/gu, '');
  return (nu.split(/['’]/).pop() ?? '').toLowerCase();
}

function sembleUneAdresse(mot: string): boolean {
  return /:\/\/|^www\.|@|\//i.test(mot);
}

function finitParUnePhrase(ligne: string): boolean {
  if (!FIN_DE_PHRASE.test(ligne)) return false;
  // "M.\nDupont", "p.\n12": the full stop of an abbreviation, not the end of a sentence.
  const mot = dernierMot(ligne);
  return !(mot.endsWith('.') && ABREVIATIONS.has(mot.slice(0, -1).toLowerCase()));
}

/** The 80th percentile of the lengths of the lines: what a "full" line is in this text. */
function longueurTypique(lignes: readonly string[]): number {
  const longueurs = lignes.map((l) => l.length).sort((a, b) => a - b);
  return longueurs[Math.max(0, Math.ceil(longueurs.length * 0.8) - 1)] ?? 0;
}

/** Does this text look like one with hard line breaks, and no paragraphs of its own? */
function aLesAllureDeLignesDures(lignes: readonly string[]): boolean {
  if (lignes.length <= 3 || lignes.some((ligne) => ligne === '')) return false;
  return lignes.some((ligne, i) => {
    const suivante = lignes[i + 1];
    return (
      suivante !== undefined && !finitParUnePhrase(ligne) && estMinuscule(premiereLettre(suivante))
    );
  });
}

function coupureEntre(
  precedente: string,
  suivante: string,
  typique: number,
  dansUneListe: boolean,
): Coupure {
  if (PUCE.test(suivante)) return dansUneListe ? 'puce' : 'paragraphe';
  const premiere = premiereLettre(suivante);
  if (TRAIT_D_UNION.test(precedente) && estMinuscule(premiere)) {
    const mot = dernierMot(precedente);
    if (sembleUneAdresse(mot)) return 'colle';
    const fragment = mot.replace(/^[^\p{L}]+/u, '').slice(0, -1);
    // Prudence: a hyphen is removed only after a plain word of three letters or more that is
    // not the start of a compound word, and only when the rest follows with no space.
    const simple = /^\p{L}{3,}$/u.test(fragment) && !PREFIXES_COMPOSES.has(fragment.toLowerCase());
    return simple ? 'mot-coupe' : 'colle';
  }
  if (estMinuscule(premiere)) return 'espace';
  if (/[,\-–]$/u.test(precedente)) return 'espace';
  if (finitParUnePhrase(precedente)) return 'paragraphe';
  const titre =
    precedente.length < typique * SEUIL_COURT &&
    estMajuscule(premiere) &&
    !MOTS_DE_LIAISON.has(dernierMotNu(precedente)) &&
    !/['’]$/u.test(precedente);
  return titre ? 'paragraphe' : 'espace';
}

/**
 * Glues back the lines of one paragraph of a text with hard line breaks; every other text is
 * returned as it is (see `aLesAllureDeLignesDures`). Only line breaks and spaces change, except
 * a hyphen that cuts a word at the end of a line, which is removed when it is sure ("informa-"
 * then "tion"). Running it again on its result changes nothing.
 */
export function recollerLignes(texte: string): string {
  const lignes = texte
    .trim()
    .split(/\r\n|\r|\n/)
    .map((ligne) => ligne.trim());
  if (!aLesAllureDeLignesDures(lignes)) return texte;

  const typique = longueurTypique(lignes);
  let sortie = lignes[0] ?? '';
  let dansUneListe = PUCE.test(sortie);
  for (let i = 1; i < lignes.length; i++) {
    const precedente = lignes[i - 1] ?? '';
    const ligne = lignes[i] ?? '';
    switch (coupureEntre(precedente, ligne, typique, dansUneListe)) {
      case 'paragraphe':
        sortie += `\n\n${ligne}`;
        dansUneListe = PUCE.test(ligne);
        break;
      case 'puce':
        sortie += `\n${ligne}`;
        break;
      case 'mot-coupe':
        sortie = sortie.replace(FIN_COUPE, '') + ligne;
        break;
      case 'colle':
        sortie += ligne;
        break;
      default:
        sortie += ` ${ligne}`;
    }
  }
  return sortie;
}

/** Does this line start with a bullet or a number ('-', '•', '1.', 'a)')? */
export function commenceParUnePuce(ligne: string): boolean {
  return PUCE.test(ligne);
}

/** The first letter of a line is an upper case letter (past opening quotes). */
export function commenceParUneMajuscule(ligne: string): boolean {
  return estMajuscule(premiereLettre(ligne));
}

/** A line that ends a sentence (not the full stop of an abbreviation). */
export function finitUnePhrase(ligne: string): boolean {
  return finitParUnePhrase(ligne);
}

/**
 * The lines of ONE paragraph, known as such (by geometry or by structure), joined with a space;
 * a hyphen that cuts a word at the end of a line is removed when it is sure, kept otherwise.
 */
export function joindreLignes(lignes: readonly string[]): string {
  let sortie = '';
  for (const [i, ligne] of lignes.entries()) {
    const brut = ligne.trim();
    if (brut === '') continue;
    const precedente = lignes[i - 1]?.trim() ?? '';
    if (sortie === '') sortie = brut;
    else if (TRAIT_D_UNION.test(precedente) && estMinuscule(premiereLettre(brut))) {
      const coupure = coupureEntre(precedente, brut, 0, false);
      sortie = coupure === 'mot-coupe' ? sortie.replace(FIN_COUPE, '') + brut : sortie + brut;
    } else {
      sortie += ` ${brut}`;
    }
  }
  return sortie;
}

/** A line that may be a title: it does not end a sentence, and the sentence does not go on after it. */
export function peutEtreUnTitre(ligne: string): boolean {
  return (
    !finitParUnePhrase(ligne) &&
    !/[,\-–'’]$/u.test(ligne) &&
    !MOTS_DE_LIAISON.has(dernierMotNu(ligne))
  );
}
